/**
 * Weapon selection: which frozen profile an attack actually fires with, plus the
 * synthetic profiles for the built-in melee control actions the attack spine resolves.
 */

import type { Combatant, EncounterState, WeaponProfile } from "@lancer-vtt/domain";
import { findAffordance, isItemDestroyed, selectWeaponProfile, weaponInstanceKey } from "@lancer-vtt/domain";
import type { RequestAttack } from "@lancer-vtt/shared";
import { SEED_OVERLAY, selectProfileByState } from "../effects";
import { NPC_ACCURACY_BY_TIER, NPC_IMPROVISED_DMG_BY_TIER } from "../npcActions";
import { ResolveError, type EffectsConfig } from "./types";

/**
 * The fallback weapon profile used when a `RequestAttack` names no weapon, or the
 * attacker froze no weapons (a pre-A2 base, or a weaponless combatant). A single
 * untyped (kinetic) d6, no tags — enough to keep an attack resolvable end-to-end
 * even without weapon data. A real attack names a weapon whose frozen profile
 * drives typed damage + mitigation.
 */
export const FALLBACK_WEAPON: WeaponProfile = {
  id: "__fallback__",
  name: "Unarmed strike",
  damage: { count: 1, sides: 6, bonus: 0, type: "kinetic" },
  ap: false,
  overkill: false,
  reliable: 0,
};

/**
 * The built-in **melee control actions** resolved through the attack spine (the melee
 * analogue of INVADE's tech-attack `act_invade` path, project `melee-control-actions`).
 * Each is an **action id** used as a `request_attack` `weapon`: the spine rolls d20 +
 * attack bonus vs **Evasion** (non-tech).
 *
 * Each carries a synthetic **threat-1** profile so the *shared* range gate
 * ({@link import("../economy").checkAttackRange}, which picks the weapon via {@link selectWeapon})
 * enforces **adjacency** — RAW "an adjacent character" — without a bespoke gate. Ram/Grapple's
 * damage descriptor is a structural placeholder (count 0) — never rolled, since the spine skips
 * the weapon-damage block for those (their consequence rides the on-hit overlay handlers keyed
 * by the action id — `RAM`/`GRAPPLE` in `seedEntries.ts` — instead). Phase 1 shipped **Ram**;
 * **Grapple** (`act_grapple`, `grapple-action` Phase 2, `decisions/0054`) joined with the
 * identical no-damage shape — **no Size gate** (Ram-only); its on-hit consequence (the mutual
 * `Grappled` bind + real `Engaged`) rides the `GRAPPLE` overlay handler.
 *
 * **Improvised Attack** (`act_improvised_attack`, RAW Core p.71, `raw-fidelity-sweep` item 2) is
 * the odd one out in this table: it's a REAL weapon-damage attack (on a hit, **1d6 kinetic** — no
 * weapon needed), not a no-damage action-id consequence. Its profile carries the real damage
 * descriptor, and the spine's weapon-damage skip (`skipWeaponDamage`, in `attack.ts`'s
 * module-private `resolveSingleTarget`) is scoped to a ZERO-count profile
 * (Ram/Grapple), not blanket `isMeleeActionAttack` — so Improvised Attack rolls its 1d6 like any
 * other weapon hit while staying adjacency-gated + vs-Evasion like Ram/Grapple. No Size gate
 * (RAW imposes none) and no on-hit overlay handler (it's a plain damage hit, nothing to trigger).
 *
 * **NPC-flavored params** (`npc-turn-control` Phase 2, decision #2): this table is the shape a PC
 * (or an unplaced/fallback) attacker fires unmodified. An **NPC** attacker's copy is derived at
 * select-time by {@link npcMeleeActionProfile} — RAW lists a flat **`+1/2/3`** tier accuracy bonus for
 * Grapple/Ram/Improvised Attack (Core p.280), a DIFFERENT figure from the NPC's own weapon Accuracy
 * (`base.attackBonus`), so it's stamped onto `attackBonus` (which the resolver already prefers over
 * `attacker.base.attackBonus` — see `WeaponProfile.attackBonus`'s doc), never added on top of it.
 * Improvised Attack additionally swaps its rolled 1d6 for the RAW flat **`3/4/6`** tier damage — the
 * same `{count:0,sides:0,bonus:N}` synthetic-profile shape the NPC weapon freeze already uses
 * (`weaponProfile.ts`'s `freezeNpcWeapon`), so the existing zero-count "flat bonus" damage path deals
 * it verbatim, no resolver change.
 */
const MELEE_ACTION_ATTACKS: Record<string, WeaponProfile> = {
  act_ram: {
    id: "act_ram",
    name: "Ram",
    damage: { count: 0, sides: 0, bonus: 0, type: "kinetic" },
    range: [{ kind: "threat", val: 1 }],
    ap: false,
    overkill: false,
    reliable: 0,
  },
  act_grapple: {
    id: "act_grapple",
    name: "Grapple",
    damage: { count: 0, sides: 0, bonus: 0, type: "kinetic" },
    range: [{ kind: "threat", val: 1 }],
    ap: false,
    overkill: false,
    reliable: 0,
  },
  act_improvised_attack: {
    id: "act_improvised_attack",
    name: "Improvised Attack",
    damage: { count: 1, sides: 6, bonus: 0, type: "kinetic" },
    range: [{ kind: "threat", val: 1 }],
    ap: false,
    overkill: false,
    reliable: 0,
  },
};

/**
 * Whether `weaponId` names a built-in **melee control action** (Ram, Grapple, Improvised Attack)
 * — a non-tech action-id attack the spine resolves vs Evasion, gathering its on-hit handlers off
 * the action id. The tech analogue is `intent.tech` (INVADE).
 */
export function isMeleeActionAttack(weaponId: string | undefined): boolean {
  return weaponId !== undefined && weaponId in MELEE_ACTION_ATTACKS;
}

/**
 * The single derivation of the **handler-gather / event-match id** for an attack fire site
 * (`system-granted-tech-attacks` D8) — the composite/action id `gatherHandlers`/
 * `gatherSystemHandlers` key their weapon-vector lookup off, and the value threaded onto
 * `AttackEvent.actionId` for `AttackMatch.actionId` to discriminate against. A tech attack
 * (INVADE, or a system-granted Full/Quick Tech action like Markerlight) or a melee action
 * attack (Ram) is not a frozen loadout weapon — `selectWeapon` can't resolve `intent.weapon`
 * to it and silently falls back to `weapons[0] ?? FALLBACK_WEAPON` — so this reads
 * `intent.weapon` directly in that case; an ordinary weapon attack keeps gathering/matching
 * off its resolved weapon's own id (`fallbackWeaponId`, the caller's already-computed
 * `selectWeapon(...).id`).
 *
 * **Every attack-family fire site must call this, not restate the ternary** —
 * `attack.ts`'s `fireAttackDeclared` and the on-hit/on-crit and on-miss
 * fires ({@link import("./attackTriggerFires").fireHitTriggers} /
 * {@link import("./attackTriggerFires").fireMissTriggers}) all fed the same live bug class
 * (rot-check §2/§3 in the
 * `system-granted-tech-attacks` tracker) before this extraction: a site that independently
 * re-derived "is this a tech/action attack" could drift from the others and gather/match a
 * DIFFERENT id than the one that actually fired.
 */
export function attackHandlerId(intent: RequestAttack, fallbackWeaponId: string): string {
  return (intent.tech || isMeleeActionAttack(intent.weapon)) && intent.weapon ? intent.weapon : fallbackWeaponId;
}

/**
 * Whether `weaponId` is one of the **no-damage** melee control actions — Ram / Grapple, whose
 * consequence is purely positional/status (Prone+push / the Grappled bind), never a damage step.
 * Named by id, NOT by the selected profile's `damage.count === 0` (`npc-turn-control` Phase 2,
 * decision #2): an NPC attacker's Improvised Attack profile is ALSO flattened to `count:0` (its RAW
 * flat tier damage, `npcMeleeActionProfile`), so a `count === 0` proxy can no longer tell "no damage
 * at all" (Ram/Grapple) apart from "flat, undiced damage" (Improvised Attack) — the id is the only
 * reliable discriminator now that both shapes share a zero dice-count.
 */
export function isNoDamageMeleeAction(weaponId: string | undefined): boolean {
  return weaponId === "act_ram" || weaponId === "act_grapple";
}

/**
 * Derive an NPC attacker's copy of a built-in melee-action profile (`npc-turn-control` Phase 2,
 * decision #2) — the flat RAW tier accuracy bonus (`+1/2/3`) stamped onto `attackBonus`, and for
 * Improvised Attack ONLY, the rolled 1d6 swapped for the flat RAW tier damage (`3/4/6`) via the same
 * `{count:0,sides:0,bonus:N}` synthetic shape `freezeNpcWeapon` already uses. Ram/Grapple keep their
 * zero-damage placeholder (their consequence rides the on-hit overlay handlers, unchanged). The tier
 * clamps to the 1–3 RAW band exactly like {@link "./npcActions".npcActionValueLabel}.
 */
function npcMeleeActionProfile(base: WeaponProfile, tier: number): WeaponProfile {
  const i = Math.max(0, Math.min(2, tier - 1));
  const attackBonus = NPC_ACCURACY_BY_TIER[i]!;
  if (base.id !== "act_improvised_attack") return { ...base, attackBonus };
  return {
    ...base,
    attackBonus,
    damage: { count: 0, sides: 0, bonus: NPC_IMPROVISED_DMG_BY_TIER[i]!, type: base.damage.type },
  };
}

/**
 * Pick the weapon profile for an attack: the named weapon off the attacker's
 * frozen list, else the first frozen weapon, else the {@link FALLBACK_WEAPON} —
 * then resolve the active **firing profile** of that weapon (effects-engine E1,
 * D6) via {@link selectWeaponProfile}: a `profileId` selects an alternate (Spin-Up,
 * a Charge-Rail tier), absent picks the weapon's default. A single-profile weapon
 * resolves as itself. The resolver never touches the compendium — weapons + their
 * profiles are all frozen at spawn (Decision 3 / D6).
 *
 * **A named `weaponId` that matches nothing throws a {@link ResolveError}** ({@link namesUnknownWeapon};
 * `select-weapon-unmatched-id`) — it never falls back to the first weapon. That fallback is how a
 * tech attack's action id (`act_invade`) once fired the first weapon's tags and range. A caller
 * holding a tech intent reads {@link techAttackProfile} instead; the room's resolve catch turns the
 * throw into an `unresolvable` reject (a GM `gm_force` naming a bad key lands there).
 *
 * **The `weapons[0]` fallback (no `weaponId` at all) is a `request_attack` invariant, not this function's own gate**
 * (`weapon-addressing-ergonomics` Phase 1, resolved decision 3): `economy.ts`'s
 * `checkAttackWeapon` — the room's pre-dice wall, mirroring `checkBarrageOpener` — hard-rejects
 * an OMITTED `weaponId` when `attacker.base.weapons` carries 2+ entries, so this fallback only
 * ever actually fires for the **sole-weapon** (auto-select the one weapon) and **weaponless**
 * ({@link FALLBACK_WEAPON} strike) cases in a gated `request_attack`/Overwatch path. Other
 * callers of `selectWeapon` that aren't behind that wall (Barrage always names a real weapon;
 * `intentCost`'s pilot Fight-cost lookup only reads `.sidearm` off whatever this resolves to,
 * never fires it) can still hit the bare fallback with 2+ weapons — harmless there since no
 * dice/damage rides on it.
 *
 * **Destroyed weapons never resolve** (`system-trauma-destruction` Phase 2, Core p.80 System
 * Trauma). A named `weaponId` whose loadout entry System Trauma destroyed throws a
 * {@link ResolveError} ({@link namesDestroyedWeapon}) — like an unknown id, it never swaps in a
 * different live weapon (a GM `gm_force`, which skips `checkAttackWeapon`'s destroyed-weapon
 * reject, lands in the room's `unresolvable` catch). The `weapons[0]` fallback (no `weaponId`)
 * skips destroyed weapons, so a mech whose only weapon was destroyed fights unarmed via
 * {@link FALLBACK_WEAPON}, exactly like a mech that never had one. Read off
 * `attacker.current.resources` via {@link isItemDestroyed} — the shared destroyed-item predicate.
 */
export function selectWeapon(
  attacker: Combatant,
  weaponId: string | undefined,
  profileId: string | undefined,
): WeaponProfile {
  // A built-in melee control action (Ram, …) is a *synthetic* weapon, not a frozen loadout
  // entry — its threat-1 profile drives the shared adjacency gate + the to-hit. Resolved before
  // the loadout so it can't be shadowed by a same-id weapon (there are none) and so a weaponless
  // attacker still rams (no fallback-to-first-weapon). It has no alternate profiles.
  const action = weaponId ? MELEE_ACTION_ATTACKS[weaponId] : undefined;
  // A talent-rewritten Improvised Attack (Brawler 2) rides the carrier's own frozen profile.
  if (action && weaponId === "act_improvised_attack" && attacker.base.improvisedAttack) return { ...action, ...attacker.base.improvisedAttack };
  if (action) return attacker.kind === "npc" ? npcMeleeActionProfile(action, attacker.tier ?? 1) : action;
  if (namesUnknownWeapon(attacker, weaponId)) {
    throw new ResolveError(`selectWeapon: ${attacker.name} has no weapon "${weaponId}" on its loadout`);
  }
  if (namesDestroyedWeapon(attacker, weaponId)) {
    throw new ResolveError(`selectWeapon: ${attacker.name}'s weapon "${weaponId}" is destroyed`);
  }
  const weapons = (attacker.base.weapons ?? []).filter((w) => !isItemDestroyed(attacker.current.resources, weaponInstanceKey(w)));
  let weapon: WeaponProfile | undefined;
  // Identity, not data: match the requested weapon by its instance key (weapon-instance-identity
  // Phase 2), so two mounts of the same compendium weapon resolve to the RIGHT physical copy. The
  // firing-profile (`profileId`) disambiguation below is separate — it picks the firing MODE.
  if (weaponId) weapon = weapons.find((w) => weaponInstanceKey(w) === weaponId);
  weapon = weapon ?? weapons[0] ?? FALLBACK_WEAPON;
  return selectWeaponProfile(weapon, profileId);
}

/**
 * Does `weaponId` name a loadout weapon System Trauma destroyed ({@link isItemDestroyed})? The
 * non-throwing half of {@link selectWeapon}'s destroyed-weapon reject, for a caller that must
 * answer before (or without) the room's `checkAttackWeapon` wall.
 */
export function namesDestroyedWeapon(attacker: Combatant, weaponId: string | undefined): boolean {
  if (!weaponId || MELEE_ACTION_ATTACKS[weaponId]) return false;
  return (attacker.base.weapons ?? []).some((w) => weaponInstanceKey(w) === weaponId && isItemDestroyed(attacker.current.resources, weaponId));
}

/**
 * Does `weaponId` name a weapon `attacker` does not carry? True only for a named id that is neither a
 * built-in melee action nor any loadout entry's instance key (destroyed ones included — naming a
 * destroyed weapon is {@link selectWeapon}'s own exclusion, not an unknown ref). A weaponless
 * attacker names nothing unknown: it still resolves the {@link FALLBACK_WEAPON} strike, the same
 * carve-out `checkAttackWeapon` keeps. The non-throwing half of {@link selectWeapon}'s reject, for a
 * caller that must answer before the room's `checkAttackWeapon` wall has run.
 */
export function namesUnknownWeapon(attacker: Combatant, weaponId: string | undefined): boolean {
  if (!weaponId || MELEE_ACTION_ATTACKS[weaponId]) return false;
  const loadout = attacker.base.weapons ?? [];
  return loadout.length > 0 && !loadout.some((w) => weaponInstanceKey(w) === weaponId);
}

/**
 * The profile a **tech attack** resolves with: no weapon at all (`tech-attack-inherits-weapon-tags`).
 * A tech attack names an ACTION id (`act_invade`, or a system-granted composite like
 * `ms_markerlight:0`) that {@link selectWeapon} cannot find, so it used to fall back to the
 * attacker's first weapon — and that weapon's tags (Accurate/Inaccurate), NPC `attackBonus` and
 * innate `accuracy`, Loading/Limited/Recharge fire bookkeeping, variable-damage prompt, area range
 * and triggered tag handlers all rode the tech attack. RAW scopes a weapon tag to "attacks made
 * with this weapon" (Core, ACCURATE), and a tech attack is not one.
 *
 * Tagless, rangeless, no accuracy of its own, and a zero-count damage placeholder the spine never
 * rolls (a tech attack skips the weapon-damage block). Its to-hit bonus is the attacker's
 * {@link techAttackBonus}, stamped onto `attackBonus` — the same synthetic-action-profile pattern
 * {@link npcMeleeActionProfile} uses for the NPC tier bonus — so `toHitBonus` (`./toHit`), which the
 * roll and the forecast both read, picks it up without a tech branch of its own. Its id is the
 * action id, so a record or forecast naming "the weapon" names the tech action rather than an
 * unrelated loadout weapon.
 */
export function techAttackProfile(intent: RequestAttack, attacker: Combatant): WeaponProfile {
  // An NPC feature's tech attack (Tear Down) prints its own bonus and Accuracy, frozen on its
  // `techAttackActions` entry; everything else rolls the attacker's tech-attack stat.
  const granted = intent.weapon !== undefined ? attacker.base.techAttackActions?.[intent.weapon] : undefined;
  // A granted tech attack whose feature carries `tg_recharge {N}` (Predatory Logic, Petrify) rides
  // its frozen affordance's Recharge target, so the fire bookkeeping discharges the SAME ledger slot
  // (`affordanceState[<key>]`, the profile id) that `request_activate` discharges and gates on.
  const recharge = granted !== undefined ? findAffordance(attacker.base.affordances, intent.weapon!)?.recharge : undefined;
  return {
    id: intent.weapon ?? "__tech__",
    name: "Tech attack",
    damage: { count: 0, sides: 0, bonus: 0, type: "kinetic" },
    ap: false,
    overkill: false,
    reliable: 0,
    attackBonus: granted?.attackBonus ?? techAttackBonus(attacker),
    ...(granted?.accuracy !== undefined ? { accuracy: granted.accuracy } : {}),
    ...(recharge !== undefined ? { recharge } : {}),
  };
}

/**
 * The bonus a **tech attack** rolls (`tech-attack-bonus-source`): a mech's frozen TECH ATTACK stat
 * (`base.techAttackBonus`, Core "TECH ATTACK … instead of GRIT"), else the frozen SYSTEMS (an NPC's
 * whole tech-attack bonus — Core NPC Quick Tech, "Invade: The NPC makes a SYSTEMS vs E-DEFENSE tech
 * attack"), else `base.attackBonus`, else `0`.
 *
 * A pilot on foot freezes `systems` = Grit (`spawn/pilot.ts`), so it rolls what it always did. A
 * mech's frozen `systems` is its raw pilot SYSTEMS skill, not a tech-attack bonus, so a mech frozen
 * before `techAttackBonus` existed (commit 5569e318) keeps rolling its Grit (`attackBonus`) — the
 * number it rolled before — until respawned, rather than dropping to raw SYSTEMS.
 */
export function techAttackBonus(attacker: Combatant): number {
  const base = attacker.base;
  if (base.techAttackBonus !== undefined) return base.techAttackBonus;
  if (attacker.kind === "mech") return base.attackBonus ?? 0;
  return base.systems ?? base.attackBonus ?? 0;
}

/**
 * The **active firing profile** an attack resolves with — the same selection {@link resolveSingleTarget}
 * makes (a named `profile` wins; else an overlay `select_profile` state lookup; else the default).
 * Kept in one place so the shared-pool roll and the per-target spine always agree on the weapon.
 * A tech attack fires no weapon: {@link techAttackProfile}.
 */
export function firingProfile(args: { state: EncounterState; attacker: Combatant; intent: RequestAttack; effects: EffectsConfig | undefined }): WeaponProfile {
  const { state, attacker, intent, effects } = args;
  if (intent.tech) return techAttackProfile(intent, attacker);
  const baseWeapon = selectWeapon(attacker, intent.weapon, undefined);
  const overlay = effects?.overlay ?? (effects ? SEED_OVERLAY : undefined);
  if (overlay && intent.profile === undefined) {
    return selectProfileByState({ state, actorId: attacker.id, weapon: baseWeapon, overlay });
  }
  return selectWeaponProfile(baseWeapon, intent.profile);
}
